import React, { useEffect, useMemo } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useUserOptions } from '../../../hooks/options/useUserOptions';
import { useAuthStore } from '../../auth/store/authStore';
import { usePermission } from '../../permissions/PermissionsContext';
import { useBulkFilteredFollowUp } from '../hooks/useBulkFilteredFollowUp';
import { TASK_PRIORITY_OPTIONS } from '../../tasks/constants';
import { contactTypeLabel } from '../contactType';
import { LAST_ACTIVITY_LABELS } from '../lastActivityLabels';
import type { ContactFilterValues } from './ContactFilterBar';

interface BulkFollowUpDrawerProps {
  open: boolean;
  onClose: () => void;
  filterValues: ContactFilterValues;
  approximateCount: number;
  countLoading: boolean;
  categoryOptions?: { value: number; label: string }[];
}

const BulkFollowUpDrawer: React.FC<BulkFollowUpDrawerProps> = ({
  open,
  onClose,
  filterValues,
  approximateCount,
  countLoading,
  categoryOptions = [],
}) => {
  const [form] = Form.useForm();
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = usePermission('administrator.access');
  const { data: users = [] } = useUserOptions();

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: u.label,
      })),
    [users]
  );

  const currentUserOption = useMemo(
    () =>
      currentUser
        ? { value: currentUser.id, label: `${currentUser.firstName} ${currentUser.lastName}` }
        : null,
    [currentUser]
  );

  const assigneeOptions = useMemo(() => {
    if (!isAdmin && currentUserOption) {
      return [currentUserOption];
    }
    return userOptions;
  }, [userOptions, isAdmin, currentUserOption]);

  const bulkMutation = useBulkFilteredFollowUp(() => {
    onClose();
  });

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({
        priority: 1,
        assigneeId: currentUser?.id,
        skipExisting: false,
      });
    }
  }, [open, form, currentUser?.id]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const dueDate = values.dueDate as dayjs.Dayjs;

      bulkMutation.mutate({
        filterCriteria: {
          search: filterValues.search || undefined,
          phone: filterValues.phone || undefined,
          categoryId: filterValues.categoryId,
          contactType:
            filterValues.contactType !== undefined
              ? Number(filterValues.contactType)
              : undefined,
          role: filterValues.role !== undefined ? Number(filterValues.role) : undefined,
          ownerUserId: filterValues.ownerUserId || undefined,
          createdFrom: filterValues.createdFrom || undefined,
          createdTo: filterValues.createdTo || undefined,
          segmentIds:
            filterValues.segmentIds?.length ? filterValues.segmentIds : undefined,
          contactCategoryIds: filterValues.contactCategoryIds?.length
            ? filterValues.contactCategoryIds
            : undefined,
          lastActivity: filterValues.lastActivity || undefined,
        },
        title: values.title,
        description: values.description ?? null,
        priority: values.priority,
        dueDate: dueDate.format('YYYY-MM-DDTHH:mm:ss') + 'Z',
        assigneeId: values.assigneeId ?? null,
        batchName: values.batchName,
        skipExisting: values.skipExisting ?? false,
      });
    } catch {
      // validation failed
    }
  };

  const activeFilterTags = useMemo(() => {
    const tags: React.ReactNode[] = [];

    if (filterValues.search) {
      tags.push(<Tag key="search">Search: {filterValues.search}</Tag>);
    }
    if (filterValues.phone) {
      tags.push(<Tag key="phone">Phone: {filterValues.phone}</Tag>);
    }
    if (filterValues.contactType !== undefined) {
      tags.push(
        <Tag key="contactType">Type: {contactTypeLabel(filterValues.contactType)}</Tag>
      );
    }
    if (filterValues.categoryId !== undefined) {
      const categoryLabel = categoryOptions.find(
        (c) => c.value === filterValues.categoryId
      )?.label ?? filterValues.categoryId;
      tags.push(<Tag key="categoryId">Category: {categoryLabel}</Tag>);
    }
    if (filterValues.role !== undefined) {
      tags.push(<Tag key="role">Role: {filterValues.role}</Tag>);
    }
    if (filterValues.ownerUserId) {
      tags.push(<Tag key="owner">Owner filter active</Tag>);
    }
    if (filterValues.createdFrom || filterValues.createdTo) {
      tags.push(<Tag key="created">Date range filter active</Tag>);
    }
    if (filterValues.segmentIds?.length) {
      tags.push(
        <Tag key="segments">{filterValues.segmentIds.length} segment(s)</Tag>
      );
    }
    if (filterValues.contactCategoryIds?.length) {
      tags.push(
        <Tag key="contactCategories">
          {filterValues.contactCategoryIds.length} contact category/categories
        </Tag>
      );
    }
    if (filterValues.lastActivity) {
      tags.push(
        <Tag key="lastActivity">
          Last Activity: {LAST_ACTIVITY_LABELS[filterValues.lastActivity] ?? filterValues.lastActivity}
        </Tag>
      );
    }

    return tags;
  }, [filterValues]);

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Bulk Create Follow-ups
          </span>
          <Typography.Text strong style={{ whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 12 }}>
            Approx. {countLoading ? '...' : approximateCount} contact(s) match
          </Typography.Text>
        </div>
      }
      open={open}
      onClose={onClose}
      width={480}
      destroyOnClose
      styles={{
        body: { padding: 24, boxSizing: 'border-box' },
        footer: { padding: '16px 24px', borderTop: '1px solid #f0f0f0' },
      }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={bulkMutation.isPending}
            >
              Create Follow-ups
            </Button>
          </Space>
        </div>
      }
    >
      {/* Filter summary */}
      <div style={{ marginBottom: 16 }}>
        <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
          Applied Filters
        </Typography.Text>
        {activeFilterTags.length > 0 ? (
          <Space wrap size={[4, 4]}>
            {activeFilterTags}
          </Space>
        ) : (
          <Typography.Text type="secondary">No filters applied (all contacts)</Typography.Text>
        )}
      </div>

      {/* Form */}
      <Form
        form={form}
        layout="vertical"
        initialValues={{ priority: 1, skipExisting: false }}
      >
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: 'Please enter a title' }]}
        >
          <Input placeholder="e.g. Call back about proposal" maxLength={200} />
        </Form.Item>

        <Form.Item name="description" label="Remarks / Notes">
          <Input.TextArea
            rows={3}
            placeholder="Optional remarks for these follow-ups"
            maxLength={2000}
          />
        </Form.Item>

        <Form.Item
          name="priority"
          label="Priority"
          rules={[{ required: true, message: 'Please select a priority' }]}
        >
          <Select options={TASK_PRIORITY_OPTIONS} />
        </Form.Item>

        <Form.Item
          name="dueDate"
          label="Follow-up Date"
          rules={[{ required: true, message: 'Please select a follow-up date' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            showTime
            disabledDate={(d) => d !== null && d.isBefore(dayjs().startOf('day'))}
          />
        </Form.Item>

        <Form.Item
          name="assigneeId"
          label={
            <span>
              Assignee{' '}
              <Typography.Text type="secondary">(optional for admins)</Typography.Text>
            </span>
          }
        >
          <Select
            showSearch
            allowClear
            placeholder={isAdmin ? 'Select a user' : 'Assigned to you'}
            disabled={!isAdmin}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
            options={assigneeOptions}
          />
        </Form.Item>

        <Form.Item
          name="batchName"
          label="Batch Name"
          rules={[{ required: true, message: 'Please enter a batch name' }]}
          tooltip="A name to identify this batch of follow-ups in the tasks list"
        >
          <Input placeholder="e.g. Q1 Follow-up Campaign" maxLength={200} />
        </Form.Item>

        <Form.Item name="skipExisting" valuePropName="checked">
          <Checkbox>
            Skip contacts with existing open/in-progress follow-ups
          </Checkbox>
        </Form.Item>
      </Form>

      {bulkMutation.data && bulkMutation.data.failedCount > 0 && (
        <Alert
          message={`${bulkMutation.data.successCount} created, ${bulkMutation.data.skippedCount} skipped, ${bulkMutation.data.failedCount} failed`}
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
    </Drawer>
  );
};

export default BulkFollowUpDrawer;
